import type { ReactNode } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

export interface StatCardProps {
  label: string;
  value: ReactNode;
  /** Signed change, e.g. `{ direction: 'up', value: '3.2%' }`. */
  trend?: { direction: 'up' | 'down'; value: string };
  /** Context for the trend, e.g. "vs last quarter". */
  caption?: string;
  /** Overrides the trend area entirely — e.g. a StatusChip. */
  footer?: ReactNode;
}

/**
 * A single KPI tile. Trend colour is semantic (success/error), not decorative,
 * and is always paired with an arrow glyph and text so it does not rely on colour alone.
 */
export function StatCard({ label, value, trend, caption, footer }: StatCardProps) {
  return (
    <Card>
      <CardContent>
        <Typography variant="overline" color="text.secondary" component="div">
          {label}
        </Typography>
        <Typography
          variant="h4"
          component="div"
          sx={{ mt: 1, fontSize: '1.875rem', lineHeight: 1.1 }}
        >
          {value}
        </Typography>
        {footer ?? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25, minHeight: 24 }}>
            {trend ? (
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  color: trend.direction === 'up' ? 'success.main' : 'error.main',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {trend.direction === 'up' ? (
                  <ArrowDropUpIcon fontSize="small" />
                ) : (
                  <ArrowDropDownIcon fontSize="small" />
                )}
                {trend.value}
              </Stack>
            ) : null}
            {caption ? (
              <Typography variant="caption" color="text.secondary">
                {caption}
              </Typography>
            ) : null}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
